import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';

/** Denormalized service-address snapshot stored on a port-in request — the
 *  address must match the losing carrier's record (rejection code 22154). */
export interface PortInServiceAddressModel {
	street1: string;
	street2?: string | null;
	city: string;
	region: string;
	postalCode: string;
	isoCountry?: string;
}

/**
 * PD-14261 — Phase 2 port-in: one row per port-in REQUEST a tenant submits to
 * bring existing carrier numbers onto Workd Voice (mirrors the Twilio Port-In
 * API parent resource). Lives in the TENANT DB like every other `softphone_*`
 * table — the submitting tenant IS the instance, so there is no instance FK;
 * the Workd cross-tenant view aggregates these at the app layer. `status`
 * tracks the Twilio parent state; per-number detail lives in `port_in_number`.
 */
@Index('port_in_request_sid_idx', ['portInRequestSid'], { unique: true })
@Entity('port_in_request')
export class PortInRequestEntity extends BaseEntity {
	/** Twilio Port-In request SID (parent). Unique. `TEST_PI_…` in test mode. */
	@Column('varchar', { name: 'portInRequestSid', length: 64 })
	public portInRequestSid!: string;

	/** The tenant user who submitted the request. */
	@Column('int', {
		name: 'fk_user__id_submitter',
		unsigned: true,
		nullable: true,
	})
	public fkUserIdSubmitter!: number | null;

	/** 'individual' | 'business' — must match the losing carrier's bill. */
	@Column('varchar', { name: 'customerType', length: 16 })
	public customerType!: string;

	/** Customer name — must match the losing-carrier bill EXACTLY (code 22153). */
	@Column('varchar', { name: 'customerName', length: 255 })
	public customerName!: string;

	/** Authorized rep — the Letter-of-Authorization e-sign recipient. */
	@Column('varchar', { name: 'authorizedRepName', length: 255 })
	public authorizedRepName!: string;

	@Column('varchar', { name: 'authorizedRepEmail', length: 255 })
	public authorizedRepEmail!: string;

	/** Denormalized service-address snapshot. */
	@Column('json', { name: 'serviceAddressJson', nullable: true })
	public serviceAddressJson!: PortInServiceAddressModel | null;

	/** Last 4 of the losing-carrier account # (audit only; full # stays at Twilio). */
	@Column('varchar', { name: 'accountNumberLast4', length: 4, nullable: true })
	public accountNumberLast4!: string | null;

	/** Self-reported losing carrier (optional). */
	@Column('varchar', { name: 'losingCarrierName', length: 255, nullable: true })
	public losingCarrierName!: string | null;

	/** Twilio AuthorizationDocument/Documents SID for the uploaded bill.
	 *  `TEST_DOC_…` in test mode. */
	@Column('varchar', {
		name: 'utilityBillDocumentSid',
		length: 64,
		nullable: true,
	})
	public utilityBillDocumentSid!: string | null;

	/** Requested FOC date (Twilio sets the actual cutover date), optional. */
	@Column('date', { name: 'targetPortDate', nullable: true })
	public targetPortDate!: string | null;

	/** Parent status — mirrors the Twilio Port-In parent state
	 *  (draft | submitted | in_progress | completed | canceled | …). */
	@Column('varchar', { name: 'status', length: 32, default: 'draft' })
	public status!: string;

	@Column('datetime', { name: 'submittedAt', nullable: true })
	public submittedAt!: Date | null;

	@Column('datetime', { name: 'signedLoaAt', nullable: true })
	public signedLoaAt!: Date | null;

	@Column('datetime', { name: 'completedAt', nullable: true })
	public completedAt!: Date | null;

	@Column('datetime', { name: 'canceledAt', nullable: true })
	public canceledAt!: Date | null;

	@Column('datetime', { name: 'lastWebhookAt', nullable: true })
	public lastWebhookAt!: Date | null;

	/**
	 * When the one-time cutover-complete notification (bell + email) was sent to
	 * the submitter (PD-14264). The poll handler fires the notification per
	 * freshly-activated number, so for a multi-number port it would otherwise
	 * send one email per number — this stamp makes it fire exactly ONCE per
	 * request (the first activated number wins; later ones see it set and skip).
	 */
	@Column('datetime', { name: 'cutoverNotifiedAt', nullable: true })
	public cutoverNotifiedAt!: Date | null;
}
